// import 'leaflet/dist/leaflet.css';

import React, { useEffect, useRef, useState } from 'react';
import { MapPin, MessageCircleMore, Navigation, Search } from 'lucide-react';
import { Form, useLoaderData, useNavigate, useSearchParams } from "@remix-run/react";
import { CreateRouteAction, haversine } from "~/src/actions/routes/CreateRouteAction";
import axios from "axios";
import type { LoaderFunction, MetaFunction } from "@remix-run/node";
import {
  ATTRACTION_TYPE_ARCHITECTURE,
  ATTRACTION_TYPE_NATURE, ATTRACTION_TYPE_RELIGION,
  LOGIN_URI_PATH,
  ROUTE_UNIT_KM,
  TRANSPORT_TYPE_BICYCLE,
  TRANSPORT_TYPE_CAR,
  TRANSPORT_TYPE_WALKING
} from "~/src/types/openTypes";
import RouteView from "~/src/components/RouteView";
import { getUserId } from "~/src/logic/UserSessionLogic";
import { trackGASubmitForm } from "~/src/logic/GoogleAnalyticsLogic";
import _mockdata_05_txt from "~/src/_data/_mockdata_05.txt?raw";
import remarkGfm from "remark-gfm";
import remarkHtml from "remark-html";
import { remark } from "remark";
import DOMPurify from 'dompurify';

export const meta: MetaFunction = () => {
  return [
    { title: "Build smart, personalized routes for your day - WayPick" },
    {
      name: "description",
      content: "Let AI do the planning - so you can focus on the experience."
    },
  ];
};

// export async function loader({ request }: LoaderFunctionArgs) {
//   const url = new URL(request.url);
//
//   // Если путь — "/m" (без слэша на конце), делаем редирект
//   if (!url.pathname.endsWith("/")) {
//     url.pathname = url.pathname + "/";
//     return redirect(url.toString());
//   }
//
//   return null;
// }

interface RouteForm {
  location: string;
  coordinates: string;
  radius: number;
  duration: number;
  transportTypes: string[];
  attractionTypes: string[];
  language: string;
  unit: "km" | "miles";
}

const waitingMessages = [
  "Analyzing your preferences...",
  "Searching the places...",
  "Finding the best possible paths...",
  "Checking for scenic routes...",
  "Optimizing travel time and stops...",
  "Evaluating data...",
  "Still working on the perfect route...",
  "Just making a few tweaks...",
  "Almost done mapping your journey...",
  "Finalizing your personalized route...",
  "Adding the finishing touches...",
  "Ready soon - thanks for your patience!"
];

const transportOptions = [
  { value: TRANSPORT_TYPE_WALKING, label: 'Walking', icon: '👣' },
  { value: TRANSPORT_TYPE_BICYCLE, label: 'Bike & E-Scooter', icon: '🚲' },
  { value: TRANSPORT_TYPE_CAR, label: 'Car', icon: '🚗' },
  // { value: TRANSPORT_TYPE_TRANSIT, label: 'Public Transport', icon: '🚌' }
];

const attractionTypes = [
  { value: ATTRACTION_TYPE_RELIGION, label: 'Religion', icon: '⛪' },
  { value: ATTRACTION_TYPE_ARCHITECTURE, label: 'Architecture', icon: '🏛️' },
  { value: ATTRACTION_TYPE_NATURE, label: 'Nature', icon: '🌿' },
  { value: 'Museum', label: 'Museums', icon: '🏛️' },
  { value: 'Monument', label: 'Monuments', icon: '🗿' },
  { value: 'Archaeology', label: 'Archaeology', icon: '🏺' },
  // { value: 'Culture', label: 'Culture', icon: '🎭' },
  { value: 'Farm', label: 'Farms', icon: '🚜️' },
  { value: 'Winery', label: 'Wineries', icon: '🍇️' },
  { value: 'Brewery', label: 'Breweries', icon: '🌾️' },
  { value: 'Distillery', label: 'Distilleries', icon: '🧪️' }
];

const languageOptions = [
  { value: 'zh', label: 'Chinese' },
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ru', label: 'Russian' },
  { value: 'es', label: 'Spanish' },
  { value: 'uk', label: 'Ukrainian' },
];

export const action = CreateRouteAction;

const GEODATA_BY_IP = new Map<string, object>();

export const loader: LoaderFunction = async ({ request }) => {
  const userId = await getUserId(request);
  // const outputText = _mockdata_05_txt;

  const forwardedFor = request.headers.get("x-forwarded-for");
  const ip = forwardedFor?.split(",")[0];
// || '95.91.250.15'

  console.log('loader');
  let geoData = null;
  if (ip) {
    try {
      console.log(ip);
      const geoDataByIp = GEODATA_BY_IP.get(ip);
      if (geoDataByIp != null) {
        geoData = geoDataByIp;
      } else {
        console.log(`http://ip-api.com/json/${ip}`);
        const geoRes = await fetch(`http://ip-api.com/json/${ip}`);
        geoData = await geoRes.json();

        if (geoData) {
          if (geoData.status === 'fail') {
            console.log(geoData);
            geoData = null;
          } else {
            GEODATA_BY_IP.set(ip, geoData);
          }
        }
      }
    } catch (err) {
      console.error('Geo API ip-api.com fetch error:', err);
    }
  }

  const html = await remark()
    .use(remarkGfm)
    .use(remarkHtml)
    .process(_mockdata_05_txt);

  // const outputHtml = html.value;
  // const outputHtml = DOMPurify.sanitize(text.toString());
  // console.log(outputHtml);
  // return { userId, outputHtml }
  return { userId, geoData }
}

interface HubspotPopupProps {
  open: boolean;
  onClose: () => void;
}

function HubspotPopup({ open, onClose }: HubspotPopupProps) {
  // useEffect(() => {
  //   if (open) {
  //     const script = document.createElement("script");
  //     script.src = "https://js.hsforms.net/forms/embed/43880434.js";
  //     script.async = true;
  //     script.onload = () => {
  //       // console.log(window);
  //       // if (window.HubSpotFormsV4) {
  //       //   window.HubSpotFormsV4.forms.create({
  //       //     region: "na1",
  //       //     portalId: "43880434",
  //       //     formId: "8b80ad4b-7cd5-4e52-a509-767faef6b347",
  //       //     target: "#hubspotForm",
  //       //   });
  //       // }
  //     };
  //     document.body.appendChild(script);
  //   }
  // }, [open]);

  // console.log(open);
  if (!open) return null;

  return (
    <div
      // className="modal-backdrop"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        // className="modal"
        style={{
          background: "white", padding: "2rem", borderRadius: "8px", maxWidth: "600px", width: "100%",
        }}
        onClick={(e) => e.stopPropagation()}
      >

        <button onClick={onClose}>Close</button>
        {/*<div id="hubspotForm"></div>*/}
        <div
          className="hs-form-frame"
          data-region="na1"
          data-form-id="8b80ad4b-7cd5-4e52-a509-767faef6b347"
          data-portal-id="43880434"
        >
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const loaderResult = useLoaderData<typeof loader>();

  const navigate = useNavigate();
  const [ searchParams, setSearchParams ] = useSearchParams();

  // const actionResult = useActionData<typeof action>();
  const [ actionResult, setActionResult ] = useState();

  const newResultsRef = useRef<HTMLDivElement>(null);

  const [ formData, setFormData ] = useState<RouteForm>({
    location: searchParams.get("location") || '',
    coordinates: '',
    radius: searchParams.get("radius") || 7,
    unit: searchParams.get("unit") || ROUTE_UNIT_KM,
    duration: searchParams.get("duration") || 5,
    transportTypes: searchParams.get("transportTypes") ? searchParams.get("transportTypes").split(",") : [ TRANSPORT_TYPE_WALKING ],
    attractionTypes: searchParams.get("attractionTypes") ? searchParams.get("attractionTypes").split(",") : [ ATTRACTION_TYPE_NATURE, ATTRACTION_TYPE_ARCHITECTURE, ATTRACTION_TYPE_RELIGION ],
    language: searchParams.get("language") || 'en'
  });

  const [ isDetectingCoordinates, setIsDetectingCoordinates ] = useState(false);
  const [ isCreatingRoute, setIsCreatingRoute ] = useState(false);
  const [ isLoadingLocationSuggestions, setIsLoadingLocationSuggestions ] = useState(false);

  const [ maxRadius, setMaxRadius ] = useState(10);

  const [ locationSuggestions, setLocationSuggestions ] = useState<any[]>([]);
  const [ isLocationSuggestionJustSelected, setIsLocationSuggestionJustSelected ] = useState(false);

  const [ waitingMessageIndex, setWaitingMessageIndex ] = useState(0);
  const waitingMessageIntervalRef = useRef(null);

  // const navigation = useNavigation();
  // const isSubmitting = navigation.state === "submitting";

  function updateSearchParams(field: string, value: string | number | string[]) {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(field, value);
    } else {
      params.delete(field);
    }

    // setSearchParams(params);
    // navigate(`?${params.toString()}`, { replace: true, preventScrollReset: true });

    const newUrl = `?${params.toString()}`;
    window.history.replaceState(null, '', newUrl);
  }

  const handleInputChange = (field: keyof RouteForm, value: string | number | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTransportToggle = (transport: string) => {
    setFormData(prev => {
      // transportTypes: prev.transportTypes.includes(transport)
      const updatedTransportTypes = prev.transportTypes.includes(transport) && transport != prev.transportTypes[0]
        ? prev.transportTypes.filter(t => t !== transport)
        : [
          // ...prev.transportTypes,
          transport
        ];

      return {
        ...prev,
        transportTypes: updatedTransportTypes
      };
    });

  };

  const handleAttractionToggle = (attraction: string) => {
    setFormData(prev => {
      const updatedAttractionTypes =
        prev.attractionTypes.includes(attraction)
          ? prev.attractionTypes.filter(a => a !== attraction)
          : [ ...prev.attractionTypes, attraction ];

      return {
        ...prev,
        attractionTypes: updatedAttractionTypes
      };
    });
  };

  const createRoute = async () => {
    // if (!loaderResult?.userId) {
    //   redirectToLogin();
    //   return;
    // }

    trackGASubmitForm(formData);

    setIsCreatingRoute(true);
    setActionResult(null);
    try {
      const res = await axios.post("/api/create-route", {
        ...formData,
        '_action': 'routes',
        'transportTypes': formData.transportTypes.join(","),
        'attractionTypes': formData.attractionTypes.join(",")
      }, {
        timeout: 180000, // 180 sec
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setActionResult(res.data);
    } catch (err) {
      console.error("Error while sending the data:", err);
      alert(err.message);
    } finally {
      setIsCreatingRoute(false);
    }

  }

  const redirectToLogin = () => {
    console.log('redirecting...'); // ← это должно напечататься
    navigate(LOGIN_URI_PATH);
  }

  const mapRefs = useRef<(HTMLDivElement | null)[]>([]);
  const mapInstances: L.Map[] = [];

  const remap = () => {
    // mapInstances.forEach((map) => map.remove());
    // mapInstances.length = 0;

    // const places = actionResult.routes[actionResult.routes.length - 1].output_parsed.places;
    // places.forEach((place, index) => {
    //   const container = mapRefs.current[index];
    //   const map = mapInstances[index];
    //
    //   if (container && !container.hasChildNodes()) {
    //     const map = L.map(container, {
    //       scrollWheelZoom: false
    //     }).setView([ place.latitude, place.longitude ], 13);
    //     L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    //       attribution: '&copy; OpenStreetMap',
    //     }).addTo(map);
    //
    //     console.log("place", index, place.name, place.latitude, place.longitude);
    //
    //     L.marker([ place.latitude, place.longitude ])
    //       .addTo(map)
    //       .bindPopup(`${place.name}`)
    //       .openPopup();
    //
    //     if (index > 0) {
    //       var pointA = [ places[index - 1].latitude, places[index - 1].longitude ];
    //       var pointB = [ place.latitude, place.longitude ];
    //
    //       L.marker(pointA)
    //         .addTo(map)
    //         .bindPopup(`${place.name}`)
    //       // .openPopup();
    //
    //       var polyline = L.polyline([ pointA, pointB ], { color: 'blue' }).addTo(map);
    //
    //       // Автоцентрирование карты по линии
    //       map.fitBounds(polyline.getBounds());
    //     }
    //
    //     mapInstances.push(map);
    //   }
    // });
  };

  // useEffect(() => {
  //   if (!actionResult?.results || !SHOWMAP) return;
  //
  //   if (mapInstances.length > 0) {
  //     remap();
  //   } else {
  //     import("leaflet").then((L) => {
  //       remap();
  //     });
  //   }
  //
  //   return () => {
  //     // it has to be here, it requests useEffect to destroy maps as soon as component is unlinked.
  //     mapInstances.forEach((map) => map.remove());
  //   };
  // }, [ actionResult ]);

  useEffect(() => {
    if (newResultsRef.current) {
      newResultsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [ actionResult ]);

  const suggestByCoordinates = async (lat, lon) => {
    handleInputChange('coordinates', [ lat, lon ].join(","));

    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
    );

    const data = await res.json();

    const location = [ data?.address?.road || null, data?.address?.city || null, data?.address?.postcode || null ].filter(i => i != null).join(", ");
    handleInputChange('location', location);
  }

  const detectLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported');
      //setLocation("Геолокация не поддерживается");
      return;
    }

    setIsDetectingCoordinates(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;

        await suggestByCoordinates(lat, lon);

        setIsDetectingCoordinates(false)
      },
      (error) => {
        setIsDetectingCoordinates(false)
        console.error(error);
        alert("Please allow access to geolocation. Error: " + error.message);
        // setLocation("Cannot get coordinates");
      });
  }

  useEffect(() => {
    const controller = new AbortController();

    const fetchSuggestions = async () => {
      if (formData.location.length < 3 || isLocationSuggestionJustSelected) {
        setLocationSuggestions([]);
        return;
      }

      setIsLocationSuggestionJustSelected(false);
      setIsLoadingLocationSuggestions(true);

      try {
        const res = await fetch(`/api/search-address?q=${encodeURIComponent(formData.location)}`, {
          signal: controller.signal,
        });

        if (!res.ok) return;

        const data = await res.json();
        setLocationSuggestions(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Ошибка запроса:", err);
        }
      } finally {
        setIsLoadingLocationSuggestions(false);
      }
    };

    const debounceTimeout = setTimeout(fetchSuggestions, 300);

    return () => {
      controller.abort();
      clearTimeout(debounceTimeout);
    };
  }, [ formData.location ]);

  useEffect(() => {
    if (isCreatingRoute) {
      waitingMessageIntervalRef.current = setInterval(() => {
        setWaitingMessageIndex((prev) => (prev + 1) % waitingMessages.length);
      }, 10000); // every 10 seconds
    }

    return () => {
      setWaitingMessageIndex(0);
      clearInterval(waitingMessageIntervalRef.current);
    }
  }, [ isCreatingRoute ]);

  useEffect(() => {
    if (!formData.transportTypes) {
      return;
    }

    switch (formData.transportTypes[0]) {
      case "Walking":
        setMaxRadius(15);
        break;
      case "Bicycle":
        setMaxRadius(50);
        break;
      case "Car":
        setMaxRadius(100);
        break;
      default:
        setMaxRadius(100);
        break;
    }
  }, [ formData.transportTypes ]);

  useEffect(() => {
    updateSearchParams("radius", formData.radius);
  }, [ formData.radius ]);

  useEffect(() => {
    updateSearchParams("duration", formData.duration);
  }, [ formData.duration ]);

  useEffect(() => {
    updateSearchParams("language", formData.language);
  }, [ formData.language ]);

  useEffect(() => {
    updateSearchParams("unit", formData.unit);
  }, [ formData.unit ]);

  useEffect(() => {
    updateSearchParams("transportTypes", formData.transportTypes);
  }, [ formData.transportTypes ]);

  useEffect(() => {
    updateSearchParams("attractionTypes", formData.attractionTypes);
  }, [ formData.attractionTypes ]);

  useEffect(() => {
    updateSearchParams("location", formData.location);
  }, [ formData.location ]);

  useEffect(() => {
    // const suggestByCoords = async () => {
    //   await
    // };

    if (loaderResult.geoData) {
      // suggestByCoordinates(loaderResult.geoData.lat, loaderResult.geoData.lon);
      handleInputChange('location', [ loaderResult.geoData.city, loaderResult.geoData.regionName, loaderResult.geoData.country ].join(', '));
      // console.log('coordinates', loaderResult.geoData.lat + ',' + loaderResult.geoData.lon);
    }
  }, []);

  return (
    <>
      {/*<style>{`*/}
      {/*  .leaflet-control-attribution a, .leaflet-control-attribution span {*/}
      {/*     display: none !important;*/}
      {/*  }*/}
      {/*`}</style>*/}

      {/*<HeroSection />*/}
      {/*<StatsSection />*/}

      {/*<div*/}
      {/*  className="text-center p-4 mb-4 text-sm text-green-800 rounded-lg bg-green-50 dark:bg-gray-800 dark:text-green-400"*/}
      {/*  role="alert"*/}
      {/*>*/}
      {/*  <span className="font-medium">Success alert!</span> Change a few things up and try submitting again.*/}
      {/*</div>*/}

      {/*<div*/}
      {/*  className="text-center p-4 mb-4 text-sm text-yellow-800 rounded-lg bg-yellow-50 dark:bg-gray-800 dark:text-yellow-300"*/}
      {/*  role="alert"*/}
      {/*>*/}
      {/*  /!*<span className="font-medium">We are in Alpha</span> *!/*/}
      {/*  We are working hard to bring more places to explore. If you*/}
      {/*  cannot find it in your location, no worries, we are on it to add.*/}
      {/*</div>*/}

      {/*<HeroSection2 />*/}
      {/*<WhoIsWayPickForSection />*/}
      {/*<KeyFeatures />*/}
      <section className="relative pt-6 overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-4">
          <p className="text-xl font-semibold text-gray-700 mb-2 leading-relaxed text-center">
            Build smart, personalized routes for your day
          </p>

          <CoverageAlert />
        </div>
      </section>

      {/* Route Form Section */}
      <section
        id="route-form"
        // className="py-16 bg-gradient-to-br from-blue-50 to-blue-100"
        className="pt-6 pb-12"
      >
        <div className="max-w-4xl mx-auto px-4">
          {/*<div className="text-center mb-12">*/}
          {/*  <h2 className="text-4xl font-bold text-gray-900 mb-4">Your Personal Route</h2>*/}
          {/*  <p className="text-lg text-gray-600">Specify your preferences and AI will create the perfect route for*/}
          {/*    you</p>*/}
          {/*</div>*/}

          <Form
            name="create-route"
            method="post"
            preventScrollReset
            autoComplete="off"
          >
            <input
              type="hidden"
              name="coordinates"
              value={formData.coordinates}
            />
            <input
              type="hidden"
              name="transportTypes"
              value={formData.transportTypes}
            />
            <input
              type="hidden"
              name="attractionTypes"
              value={formData.attractionTypes}
            />
            <input
              type="hidden"
              name="language"
              value={languageOptions.filter(i => i.value == formData.language)[0].label}
            />

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="space-y-6">


                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Location Input */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      📍 Your location
                    </label>
                    {isDetectingCoordinates ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black mr-3"></div>
                        Retrieving your location...
                      </div>
                    ) : (
                      // <div className="relative flex items-center gap-2">
                      <div className="relative">

                        <div className="relative flex items-center gap-2">
                          <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={formData.location}
                            name="location"
                            onChange={(e) => {
                              setIsLocationSuggestionJustSelected(false);
                              handleInputChange('coordinates', '');
                              handleInputChange('location', e.target.value);
                            }}
                            placeholder="Enter city or address"
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          />

                          <div className="flex items-center pr-2">
                            <button
                              type="button"
                              onClick={detectLocation}
                              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
                            >
                              <Navigation className="w-4 h-4 text-gray-500" />
                            </button>
                          </div>
                        </div>


                        {isLoadingLocationSuggestions && (
                          <div className="absolute right-3 top-3 h-5 w-5 animate-spin rounded-full border-2 border-t-transparent border-gray-400"></div>
                        )}

                        {locationSuggestions.length > 0 && (
                          <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {locationSuggestions.map((item) => (
                              <li
                                key={item.place_id}
                                className="px-4 py-2 hover:bg-blue-100 cursor-pointer text-sm text-gray-800"
                                onClick={() => {
                                  const coordinates = item.lat + "," + item.lon;
                                  setIsLocationSuggestionJustSelected(true);
                                  handleInputChange('coordinates', coordinates);
                                  handleInputChange('location', item.display_name);
                                  setLocationSuggestions([]);
                                }}
                              >
                                {item.display_name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Radius Input */}
                  <div className="flex gap-2">
                    <div className="w-4/6">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        🎯 Search radius: {formData.radius} {formData.unit}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max={maxRadius}
                        value={formData.radius}
                        name="radius"
                        onChange={(e) => handleInputChange('radius', parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-blue"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>1 {formData.unit}</span>
                        <span>{maxRadius} {formData.unit}</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <label className="text-xs ml-2 text-gray-700">
                        <input
                          type="radio"
                          name="unit"
                          value="km"
                          checked={formData.unit === "km"}
                          onChange={(e) => handleInputChange('unit', e.target.value)}
                        />
                        {' '} km
                      </label>
                      <label className="text-xs ml-2 text-gray-700">
                        <input
                          type="radio"
                          name="unit"
                          value="miles"
                          checked={formData.unit === "miles"}
                          onChange={(e) => handleInputChange('unit', e.target.value)}
                        />
                        {' '} miles
                      </label>
                    </div>
                  </div>


                  {/* Duration Input */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ⏰ Route duration: {formData.duration} h
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="12"
                      value={formData.duration}
                      name="duration"
                      onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-blue"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>1 hour</span>
                      <span>12 hours</span>
                    </div>
                  </div>

                  {/* Language Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🌐 Route language
                    </label>
                    <select
                      value={formData.language}
                      onChange={(e) => handleInputChange('language', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      {languageOptions.map(option => (
                        <option
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Transport Options */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    🚶‍♂️ Mode of transport
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {transportOptions.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        //
                        onClick={() => {
                          handleTransportToggle(option.value);
                        }}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.transportTypes.includes(option.value)
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="text-lg mb-1">{option.icon}</div>
                        <div className="text-sm font-medium text-gray-700">{option.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
                {/*<div className="text-sm text-gray-600">*/}
                {/*  You selected: {formData.transportTypes.join(", ") || "None"}*/}
                {/*</div>*/}

                {/* Attraction Type */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    🎨 Type of attractions
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {attractionTypes.map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleAttractionToggle(type.value)}
                        className={`p-3 rounded-xl border-2 transition-all text-left ${
                          formData.attractionTypes.includes(type.value)
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="text-lg mb-1">{type.icon}</div>
                        <div className="text-sm font-medium text-gray-700">{type.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
                {/*<div className="text-sm text-gray-600">*/}
                {/*  You selected: {formData.attractionTypes.join(", ") || "None"}*/}
                {/*</div>*/}
              </div>

              <button
                // type="submit"
                type="button"
                name="_action"
                value="routes"
                onClick={createRoute}
                disabled={!formData.location || !formData.coordinates || isCreatingRoute}
                className="w-full mt-8 bg-blue-600 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:bg-blue-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isCreatingRoute ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                    {waitingMessages[waitingMessageIndex]}
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Search className="w-5 h-5 mr-2" />
                    Discover routes (takes up to 2 min)
                    {/*Create Route*/}
                  </div>
                )}
              </button>
              <button
                id="leave-feedback"
                type="button"
                data-tally-open="w2992j"
                data-tally-emoji-text="👋"
                data-tally-emoji-animation="wave"
                className="w-full mt-8 bg-blue-600 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:bg-blue-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <div className="flex items-center justify-center">
                  <MessageCircleMore className="w-5 h-5 mr-2" />
                  Leave Feedback
                </div>
              </button>
            </div>
          </Form>
        </div>
      </section>

      {loaderResult.outputHtml && (
        <div
          className="prose prose-lg max-w-none mx-auto p-4"
          dangerouslySetInnerHTML={{
            __html: loaderResult.outputHtml,
          }}
        >
        </div>
      )}

      {/* Results Section */}
      {actionResult?.results.length == 0 && (
        <section
          ref={newResultsRef}
          className="py-16 max-w-2xl mx-auto"
        >
          <div className="text-center text-gray-900">
            <h2 className="text-2xl font-bold">
              No places found within the requested radius.
            </h2>
            <CoverageAlert />
          </div>
        </section>
      )}

      {actionResult?.results.length > 0 && actionResult?.results[0].routes.length == 0 && (
        <section
          ref={newResultsRef}
          className="py-16 max-w-2xl mx-auto"
        >
          <div className="text-center text-gray-900">
            <h2 className="text-2xl font-bold">
              No routes found within the requested duration.
            </h2>
            <CoverageAlert />
          </div>
        </section>
      )}

      {actionResult?.results && actionResult?.results.length > 0 && actionResult?.results[0].routes.length > 0 && (
        <section
          ref={newResultsRef}
          className={`py-8 bg-white grid grid-cols-1 md:grid-cols-${actionResult?.results[0].routes.length} max-w-6xl mx-auto`}
        >
          {actionResult?.results[0].routes.map((route, index) => (
            <div
              key={index}
              className="max-w-2xl mx-auto px-4 mb-8"
            >
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Your personal
                  route <br /> {actionResult?.results[0].routes.length > 1 ? index + 1 : ''}</h2>
                {/*<div className="flex items-center justify-center space-x-6 text-gray-600">*/}
                {/*  <div className="flex items-center">*/}
                {/*    <Timer className="w-5 h-5 mr-2" />*/}
                {/*    <span>{route.totalDuration}, {route.totalDistance}</span>*/}
                {/*  </div>*/}
                {/*  <div className="flex items-center">*/}
                {/*    <Route className="w-5 h-5 mr-2" />*/}
                {/*    <span>{route.places.length - 2}</span>*/}
                {/*  </div>*/}
                {/*</div>*/}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-1 gap-2">
                {/* Route Steps - Styled like the screenshot */}
                <RouteView
                  route={route}
                  embedded={false}
                />

                {/*<Link*/}
                {/*  to={`/routes/${route._id}`}*/}
                {/*  target="_blank"*/}
                {/*>*/}
                {/*</Link>*/}

                {/*<button*/}
                {/*  type="button"*/}
                {/*  onClick={() => {*/}
                {/*    document.getElementById('route-form')?.scrollIntoView({ behavior: 'smooth' });*/}
                {/*  }}*/}
                {/*  className="w-full mt-6 bg-blue-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-blue-700 transition-all"*/}
                {/*  // onClick={() => document.getElementById('route-form')?.scrollIntoView({ behavior: 'smooth' })}*/}
                {/*>*/}
                {/*  Not suitable? Get another route*/}
                {/*</button>*/}


                {/*Map Placeholder */}
                {/*<div className="lg:sticky lg:top-24 h-fit">*/}
                {/*  <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl p-8 h-96 flex items-center justify-center">*/}
                {/*    <div className="text-center">*/}
                {/*      <MapIcon className="w-16 h-16 text-blue-600 mx-auto mb-4" />*/}
                {/*      <h3 className="text-xl font-semibold text-gray-700 mb-2">Интерактивная карта</h3>*/}
                {/*      <p className="text-gray-600">Здесь будет отображаться ваш маршрут с точками на карте</p>*/}
                {/*    </div>*/}
                {/*  </div>*/}

                {/*  /!* Route Summary *!/*/}
                {/*  <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6">*/}
                {/*    <h4 className="text-lg font-semibold text-gray-900 mb-4">Route Summary</h4>*/}
                {/*    <div className="space-y-3">*/}
                {/*      <div className="flex justify-between">*/}
                {/*        <span className="text-gray-600">Total time:</span>*/}
                {/*        <span className="font-semibold">{Math.floor(getTotalDuration() / 60)}ч {getTotalDuration() % 60}м</span>*/}
                {/*      </div>*/}
                {/*      <div className="flex justify-between">*/}
                {/*        <span className="text-gray-600">Number of points:</span>*/}
                {/*        <span className="font-semibold">{actionResult.results[0].output_parsed.waypoints.length}</span>*/}
                {/*      </div>*/}
                {/*      <div className="flex justify-between">*/}
                {/*        <span className="text-gray-600">Mode of transport:</span>*/}
                {/*        <span className="font-semibold">*/}
                {/*          {formData.transportTypes.map(t =>*/}
                {/*            transportOptions.find(opt => opt.value === t)?.label*/}
                {/*          ).join(', ')}*/}
                {/*        </span>*/}
                {/*      </div>*/}
                {/*      <div className="flex justify-between">*/}
                {/*        <span className="text-gray-600">Language:</span>*/}
                {/*        <span className="font-semibold">*/}
                {/*          {languageOptions.find(l => l.value === formData.language)?.label}*/}
                {/*        </span>*/}
                {/*      </div>*/}
                {/*      <div className="flex justify-between">*/}
                {/*        <span className="text-gray-600">Average rating:</span>*/}
                {/*        <span className="font-semibold flex items-center">*/}
                {/*          <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />*/}
                {/*          {(routeSteps.reduce((sum, step) => sum + step.rating, 0) / routeSteps.length).toFixed(1)}*/}
                {/*        </span>*/}
                {/*      </div>*/}
                {/*    </div>*/}

                {/*    <button*/}
                {/*      className="w-full mt-6 bg-blue-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-blue-700 transition-all"*/}
                {/*      onClick={() => document.getElementById('route-form')?.scrollIntoView({ behavior: 'smooth' })}*/}
                {/*    >*/}
                {/*      New Search*/}
                {/*    </button>*/}
                {/*  </div>*/}
                {/*</div>*/}
              </div>
            </div>
          ))}
        </section>
      )}
    </>
  );
}

function CoverageAlert() {
  const [ isPopupOpen, setIsPopupOpen ] = useState(false);

  return (
    <div
      className="text-left p-4 text-sm text-blue-800 rounded-lg bg-blue-50 dark:bg-gray-800 dark:text-blue-400"
      role="alert"
    >
      {/*<span className="font-medium">Info alert!</span> */}
      🛠 We're actively expanding our coverage to reach more neighborhoods.
      <br />
      <br />
      📍 Currently, we cover the following locations:

      <div className="grid grid-cols-2 md:grid-cols-4 pt-2 gap-3">
        <div>
          Canada
          <br /> • Alberta
        </div>
        <div>
          Canary Islands
          <br /> • Tenerife
        </div>
        <div>
          Germany
          <br /> • Bayern
        </div>
        {/*<div>*/}
        {/*  Israel*/}
        {/*  <br /> • Tel Aviv*/}
        {/*</div>*/}
        <div>
          Portugal
          <br /> • Lissabon
          <br /> • Porto
        </div>
        <div>
          Spain
          <br /> • Girona (region)
        </div>
        <div>
          United Kingdom
          <br /> • West Sussex
        </div>
      </div>

      <br />

      🚀 Want to get early access in your area?
      <br />
      <button
        className="underline"
        onClick={() => setIsPopupOpen(true)}
      >Let us know
      </button>
      {' '}
      about a location - and we'll notify you as soon as we launch
      nearby!

      <HubspotPopup
        open={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
      />

      {/*Edmonton (Canada), Munich (Germany), Tel Aviv (Israel), West Sussex (UK)*/}
    </div>
  );
}